// =============================================================================
// The product detail page's three interactive pieces: the tab strip, the
// gallery thumbnails, and the quantity stepper.
//
// Each is wired to its own container rather than to the document, because the
// related-products row and the site editor's live preview can both put more than
// one of these on a page at once.
// =============================================================================

import { readQty, writeQty } from "./qty.js";

/** Arrow-key direction per key, matching the reading order of the tab strip. */
const ARROW_STEPS = { ArrowLeft: -1, ArrowRight: 1 };

export default function initProductDetail() {
	initTabs();
	initGallery();
	initQtySteppers();
}

/**
 * Tab strips.
 *
 * PHP renders the first tab open and pairs every tab with its panel by id, so
 * the only thing missing is the behaviour — and the roving tabindex that
 * `role="tablist"` implies: the strip is one tab stop, and the arrow keys move
 * inside it.
 */
function initTabs() {
	document.querySelectorAll(".pd-tabs").forEach((tabs) => {
		const buttons = [...tabs.querySelectorAll('[role="tab"]')];
		const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];

		if (buttons.length === 0) {
			return;
		}

		const select = (button) => {
			buttons.forEach((other) => {
				const isSelected = other === button;

				other.classList.toggle("is-active", isSelected);
				other.setAttribute("aria-selected", isSelected ? "true" : "false");
				other.tabIndex = isSelected ? 0 : -1;
			});

			// The panel swap is guarded on the pairing actually resolving. If an
			// id ever stopped matching, clearing every panel would hide the
			// product's description outright — a far worse failure than leaving
			// the panels alone.
			const controls = button.getAttribute("aria-controls");
			const target = controls ? document.getElementById(controls) : null;

			if (target && panels.includes(target)) {
				panels.forEach((panel) => panel.classList.toggle("is-active", panel === target));
			}
		};

		buttons.forEach((button) => {
			button.addEventListener("click", () => select(button));
		});

		tabs.addEventListener("keydown", (event) => {
			const current = buttons.indexOf(document.activeElement);

			if (current === -1) {
				return;
			}

			let next;

			if (event.key in ARROW_STEPS) {
				next = (current + ARROW_STEPS[event.key] + buttons.length) % buttons.length;
			} else if (event.key === "Home") {
				next = 0;
			} else if (event.key === "End") {
				next = buttons.length - 1;
			} else {
				return;
			}

			event.preventDefault();
			buttons[next].focus();
			select(buttons[next]);
		});

		// Establishes the initial roving tabindex from whatever PHP marked
		// active, so the first Tab into the strip lands on the open tab rather
		// than on the first one.
		select(tabs.querySelector('[role="tab"].is-active') || buttons[0]);
	});
}

/**
 * Gallery thumbnails.
 *
 * Each thumb carries the large crop in `data-pd-full`; its own `<img>` is the
 * small one, so the strip stays cheap to load and the panel still gets full
 * resolution art when a thumb is chosen.
 */
function initGallery() {
	document.querySelectorAll(".pd-gallery").forEach((gallery) => {
		const main = gallery.querySelector("[data-pd-main]");
		const thumbs = [...gallery.querySelectorAll("[data-pd-thumb]")];

		if (!main || thumbs.length === 0) {
			return;
		}

		thumbs.forEach((thumb) => {
			thumb.addEventListener("click", () => {
				const src = thumb.dataset.pdFull;

				if (!src) {
					return;
				}

				thumbs.forEach((other) => other.classList.toggle("is-active", other === thumb));
				main.src = src;
			});
		});
	});
}

/** Quantity steppers. */
function initQtySteppers() {
	document.querySelectorAll("[data-qty]").forEach((field) => {
		const scope = field.closest(".qty-selector");

		if (!scope) {
			return;
		}

		scope.querySelectorAll("[data-qty-step]").forEach((button) => {
			button.addEventListener("click", () => {
				const step = Number(button.dataset.qtyStep);

				if (!Number.isFinite(step) || step === 0) {
					return;
				}

				writeQty(field, readQty(scope) + step);
			});
		});

		// The field takes free text, so a typed value is only normalised once
		// the visitor is done with it — clamping on every keystroke would fight
		// someone entering "12" by way of "1".
		field.addEventListener("change", () => writeQty(field, readQty(scope)));
	});
}
